import apiClient from './client';
import type {
  KgBetelSuggestion,
  KgChild,
  KgChildPayload,
  KgEnrollPayload,
  KgEnrollment,
  KgEnrollmentUpdatePayload,
  KgFamily,
  KgFamilyFilters,
  KgFamilyPayload,
  KgGroup,
  KgGroupPayload,
  KgGuardian,
  KgGuardianPayload,
  KgRolloverPayload,
  KgRolloverPreview,
  KgRolloverResult,
  KgSchoolYear,
  KgSchoolYearPayload,
} from '@/types/kindergarten';

const BASE = '/kindergarten';

interface Paginated<T> {
  data: T[];
  meta: { current_page: number; last_page: number; per_page: number; total: number };
}

// ─── School years ───

export async function listSchoolYears(): Promise<KgSchoolYear[]> {
  const { data } = await apiClient.get(`${BASE}/school-years`);
  return data.data;
}

export async function getSchoolYear(id: string): Promise<KgSchoolYear> {
  const { data } = await apiClient.get(`${BASE}/school-years/${id}`);
  return data.data;
}

export async function createSchoolYear(payload: KgSchoolYearPayload): Promise<KgSchoolYear> {
  const { data } = await apiClient.post(`${BASE}/school-years`, payload);
  return data.data;
}

export async function updateSchoolYear(id: string, payload: Partial<KgSchoolYearPayload>): Promise<KgSchoolYear> {
  const { data } = await apiClient.put(`${BASE}/school-years/${id}`, payload);
  return data.data;
}

export async function makeSchoolYearCurrent(id: string): Promise<KgSchoolYear> {
  const { data } = await apiClient.post(`${BASE}/school-years/${id}/make-current`);
  return data.data;
}

export async function deleteSchoolYear(id: string): Promise<void> {
  await apiClient.delete(`${BASE}/school-years/${id}`);
}

// ─── Rollover ("Pornire an școlar") ───

export async function previewRollover(fromId: string, toSchoolYearId: string): Promise<KgRolloverPreview> {
  const { data } = await apiClient.post(`${BASE}/school-years/${fromId}/rollover/preview`, { to_school_year_id: toSchoolYearId });
  return data.data;
}

export async function createRolloverGroups(fromId: string, toSchoolYearId: string): Promise<KgGroup[]> {
  const { data } = await apiClient.post(`${BASE}/school-years/${fromId}/rollover/groups`, { to_school_year_id: toSchoolYearId });
  return data.data;
}

export async function runRollover(fromId: string, payload: KgRolloverPayload): Promise<KgRolloverResult> {
  const { data } = await apiClient.post(`${BASE}/school-years/${fromId}/rollover`, payload);
  return data.data;
}

// ─── Groups ───

export async function listGroups(schoolYearId?: string): Promise<KgGroup[]> {
  const { data } = await apiClient.get(`${BASE}/groups`, { params: schoolYearId ? { school_year_id: schoolYearId } : {} });
  return data.data;
}

export async function getGroup(id: string): Promise<KgGroup> {
  const { data } = await apiClient.get(`${BASE}/groups/${id}`);
  return data.data;
}

export async function getGroupRoster(id: string, status?: string): Promise<KgEnrollment[]> {
  const { data } = await apiClient.get(`${BASE}/groups/${id}/roster`, { params: status ? { status } : {} });
  return data.data;
}

export async function createGroup(payload: KgGroupPayload): Promise<KgGroup> {
  const { data } = await apiClient.post(`${BASE}/groups`, payload);
  return data.data;
}

export async function updateGroup(id: string, payload: Partial<KgGroupPayload>): Promise<KgGroup> {
  const { data } = await apiClient.put(`${BASE}/groups/${id}`, payload);
  return data.data;
}

export async function deleteGroup(id: string): Promise<void> {
  await apiClient.delete(`${BASE}/groups/${id}`);
}

// ─── Families ───

export async function listFamilies(filters: KgFamilyFilters = {}): Promise<Paginated<KgFamily>> {
  const { data } = await apiClient.get(`${BASE}/families`, { params: filters });
  return data;
}

export async function getFamily(id: string): Promise<KgFamily> {
  const { data } = await apiClient.get(`${BASE}/families/${id}`);
  return data.data;
}

export async function createFamily(payload: KgFamilyPayload): Promise<KgFamily> {
  const { data } = await apiClient.post(`${BASE}/families`, payload);
  return data.data;
}

export async function updateFamily(id: string, payload: Partial<KgFamilyPayload>): Promise<KgFamily> {
  const { data } = await apiClient.put(`${BASE}/families/${id}`, payload);
  return data.data;
}

export async function deleteFamily(id: string): Promise<void> {
  await apiClient.delete(`${BASE}/families/${id}`);
}

export async function linkBetelFamily(id: string, betelFamilyId: string | null): Promise<KgFamily> {
  const { data } = await apiClient.post(`${BASE}/families/${id}/link-betel`, { betel_family_id: betelFamilyId });
  return data.data;
}

export async function suggestBetelFamilies(q: string): Promise<KgBetelSuggestion[]> {
  const { data } = await apiClient.get(`${BASE}/families/suggest-betel`, { params: { q } });
  return data.data;
}

// ─── Guardians ───

export async function searchGuardians(q: string): Promise<KgGuardian[]> {
  const { data } = await apiClient.get(`${BASE}/guardians/search`, { params: { q } });
  return data.data;
}

export async function addGuardian(
  familyId: string,
  payload: KgGuardianPayload | { guardian_id: string; relationship?: string; is_primary_contact?: boolean },
): Promise<KgGuardian> {
  const { data } = await apiClient.post(`${BASE}/families/${familyId}/guardians`, payload);
  return data.data;
}

export async function updateGuardianPivot(
  familyId: string,
  guardianId: string,
  payload: { relationship?: string; is_primary_contact?: boolean },
): Promise<KgGuardian> {
  const { data } = await apiClient.put(`${BASE}/families/${familyId}/guardians/${guardianId}`, payload);
  return data.data;
}

export async function detachGuardian(familyId: string, guardianId: string): Promise<void> {
  await apiClient.delete(`${BASE}/families/${familyId}/guardians/${guardianId}`);
}

export async function updateGuardian(id: string, payload: Partial<KgGuardianPayload> & { is_active?: boolean; member_id?: string | null }): Promise<KgGuardian> {
  const { data } = await apiClient.put(`${BASE}/guardians/${id}`, payload);
  return data.data;
}

// ─── Children + enrollments ───

export async function addChild(familyId: string, payload: KgChildPayload): Promise<KgChild> {
  const { data } = await apiClient.post(`${BASE}/families/${familyId}/children`, payload);
  return data.data;
}

export async function getChild(id: string): Promise<KgChild> {
  const { data } = await apiClient.get(`${BASE}/children/${id}`);
  return data.data;
}

export async function updateChild(id: string, payload: Partial<KgChildPayload>): Promise<KgChild> {
  const { data } = await apiClient.put(`${BASE}/children/${id}`, payload);
  return data.data;
}

export async function deleteChild(id: string): Promise<void> {
  await apiClient.delete(`${BASE}/children/${id}`);
}

export async function enrollChild(childId: string, payload: KgEnrollPayload): Promise<KgEnrollment> {
  const { data } = await apiClient.post(`${BASE}/children/${childId}/enroll`, payload);
  return data.data;
}

export async function updateEnrollment(id: string, payload: KgEnrollmentUpdatePayload): Promise<KgEnrollment> {
  const { data } = await apiClient.put(`${BASE}/enrollments/${id}`, payload);
  return data.data;
}

export async function withdrawEnrollment(id: string, withdrawnOn: string, notes?: string): Promise<KgEnrollment> {
  const { data } = await apiClient.post(`${BASE}/enrollments/${id}/withdraw`, { withdrawn_on: withdrawnOn, notes });
  return data.data;
}

export async function reactivateEnrollment(id: string): Promise<KgEnrollment> {
  const { data } = await apiClient.post(`${BASE}/enrollments/${id}/reactivate`);
  return data.data;
}

// ─── CRM links (registry pages) ───

export async function getKindergartenForBetelFamily(familyId: string): Promise<KgFamily[]> {
  const { data } = await apiClient.get(`/families/${familyId}/kindergarten`);
  return data.data;
}

export async function getKindergartenForMember(memberId: string): Promise<{ as_guardian: KgGuardian[]; as_child: KgChild[] }> {
  const { data } = await apiClient.get(`/members/${memberId}/kindergarten`);
  return data.data;
}
